import React, { useState, useEffect, useRef } from "react";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { FloatLabel } from "primereact/floatlabel";
import GlobalTable from "../components/GlobalTable"; // Assuming GlobalTable is your table component
import axiosInstance from '../services/axiosInstance';
import { toYmd } from '../utils/dateOnly';
import { handleApiError } from "../components/apiErrorHandler";
import { ProductSelectorInput } from "../components/ProductSelectorInput";

const AuditInventory = () => {
    const [auditData, setAuditData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        startDate: null,
        endDate: null,
        product: null,
        user: null,
    });
    const productRef = useRef(null);
    const [productQuery, setProductQuery] = useState('');
    const [users, setUsers] = useState([]);

    // if a product object gets set into filters, reflect its name in the input
    useEffect(() => {
        if (filters.product && typeof filters.product === 'object') {
            setProductQuery(filters.product.name || '');
        }
    }, [filters.product]);

    // Fetch audit records
    const fetchAuditRecords = async (filterParams = {}) => {
        setLoading(true)
        try {
            const response = await axiosInstance.get("/api/audit/", { params: filterParams });
            setAuditData(response.data);
            setLoading(false);
        } catch (error) {
            handleApiError(error);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    // Fetch products and users for filters (optional)
    const fetchDropdownData = async () => {
        try {
            const userResponse = await axiosInstance.get("/api/orguser/");
            setUsers(userResponse.data);
        } catch (error) {
            handleApiError(error);
        }
    };

    // Handle filter change
    const handleFilterChange = (key, value) => {
        setFilters({ ...filters, [key]: value });
        console.log(key, value);
    };

    // Apply filters
    const applyFilters = () => {
        // Check if the date range exceeds 90 days
        if (filters.startDate && filters.endDate) {
            const startDate = new Date(filters.startDate);
            const endDate = new Date(filters.endDate);
            const differenceInDays = (endDate - startDate) / (1000 * 60 * 60 * 24);
    
            if (differenceInDays > 90) {
                handleApiError({Error: "The date range for search cannot exceed 90 days. Please adjust the dates."});
                return; // Stop execution if the range is invalid
            }
        }
    
        const params = {
            start_date: filters.startDate ? toYmd(filters.startDate) : undefined,
            end_date: filters.endDate ? toYmd(filters.endDate) : undefined,
            product_id: filters.product?.id,
            user_id: filters.user?.id,
        };
        fetchAuditRecords(params);
        console.log('Prduct Obj: ', filters);
    };
    
    // Initial load
    useEffect(() => {
        fetchAuditRecords(); 
        fetchDropdownData(); 
    }, []);

    const columns=[
        { accessor: "timestamp", Header: "TimeStamp", dataType: 'date', sortable: true },
        { accessor: "action", Header: "Action", sortable: true },
        { accessor: "user_name", Header: "User", sortable: true },
        { accessor: "product_name", Header: "Product" },
        { accessor: "batch_num", Header: "Batch" },
        { accessor: "dept_name", Header: "Department" },
        { accessor: "previous_quantity", Header: "Previous Quantity" },
        { accessor: "new_quantity", Header: "New Quantity" },
        { accessor: "previous_reorder_level", Header: "Previous Reorder Level" },
        { accessor: "new_reorder_level", Header: "New Reorder Level" },
        { accessor: "reference", Header: "Reference" },
        { accessor: "remarks", Header: "Remarks" },
        { accessor: "operator_name", Header: "Invoice Operator" },
    ];
    return (
        <div>
            <div className="containers">
            <div className="grid" style={{ gap: '2rem', padding: '1rem' }}>
            <div className="sm:col-6 md:col-4 lg:col-2">
                <FloatLabel>
                        <Calendar
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange("startDate", e.value)}
                            dateFormat="yy-mm-dd"
                            showIcon
                        />
                        <label>Start Date:</label>
                        </FloatLabel>
                    </div>
                    <div className="sm:col-6 md:col-4 lg:col-2">
                    <FloatLabel>
                        <Calendar
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange("endDate", e.value)}
                            dateFormat="yy-mm-dd"
                            showIcon
                            minDate={filters.startDate}
                            maxDate={filters.startDate ? new Date(new Date(filters.startDate).getTime() + 90 * 24 * 60 * 60 * 1000) : null}                        
                        />
                        <label>End Date:</label>
                        </FloatLabel>
                    </div>
                    <div className="sm:col-6 md:col-4 lg:col-2">
                    <FloatLabel>
                                <ProductSelectorInput
                                    id="filter-product"
                                    ref={productRef}
                                    query={productQuery}
                                    onQueryChange={(newQuery) => {
                                        setProductQuery(newQuery);            // ← was setProducts(newQuery)
                                        if (filters.product) {
                                        // if they start typing again, clear the selected object
                                        handleFilterChange('product', null);
                                        }
                                    }}
                                    onSelect={(productObj) => {
                                        handleFilterChange('product', productObj); // keep the object for product_id
                                        setProductQuery(productObj?.name ?? '');
                                    }}
                                    placeholder="Type in Product"
                                /><label>Product:</label>
                                </FloatLabel>
                    </div>
                    <div className="sm:col-6 md:col-4 lg:col-2">
                    <FloatLabel>
                        <Dropdown
                            value={filters.user}
                            options={users}
                            onChange={(e) => handleFilterChange("user", e.value)}
                            optionLabel="username"
                            placeholder="Select User"
                            showClear
                        />
                        <label>User:</label>
                        </FloatLabel>
                    </div>
                </div>
                <div className="grid" style={{ gap: '1rem', padding: '1rem' }}>
                    <Button label="Search" outlined icon="pi pi-search" onClick={applyFilters} />
                    <Button label="Clear" outlined icon="pi pi-eraser" onClick={() => setFilters({ startDate: null, endDate: null, product: null, user: null })} />
                </div>
            </div>
            <div className="containers">
            <GlobalTable
                columns={columns}
                data={auditData}
                tabletitle="Inventory Audit List"
                loading={loading}
                showEditColumn={false}
            />
        </div></div>
    );
};

export default AuditInventory;
